import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

export type FinancialsByStateFilters = {
  start_date: string;
  end_date: string;
};

export type FinancialsStateRow = {
  name: string;
  abbr: string;
  withdraw: number;
  revenue: number;
  deposit: number;
};

type RawFinancialsStateRow = {
  name?: string | null;
  state?: string | null;
  abbr?: string | null;
  withdraw?: number | string | null;
  withdrawal?: number | string | null;
  withdrawal_amount?: number | string | null;
  revenue?: number | string | null;
  deposit?: number | string | null;
  deposit_amount?: number | string | null;
};

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const isFinancialsStateEntry = (value: unknown): value is RawFinancialsStateRow =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  ("withdraw" in value ||
    "withdrawal" in value ||
    "withdrawal_amount" in value ||
    "revenue" in value ||
    "deposit" in value ||
    "deposit_amount" in value);

const mapEntryToRow = (
  fallbackName: string,
  fallbackAbbr: string,
  entry: RawFinancialsStateRow,
): FinancialsStateRow => {
  const abbr =
    (typeof entry.abbr === "string" && entry.abbr.trim()) ||
    (typeof entry.state === "string" && entry.state.trim().toUpperCase()) ||
    fallbackAbbr;
  const name =
    (typeof entry.name === "string" && entry.name.trim()) || fallbackName;

  return {
    name,
    abbr: abbr.toUpperCase(),
    withdraw: coerceNumber(
      entry.withdraw ?? entry.withdrawal_amount ?? entry.withdrawal,
    ),
    revenue: coerceNumber(entry.revenue),
    deposit: coerceNumber(entry.deposit ?? entry.deposit_amount),
  };
};

/**
 * GET /financials/analytics/kpis/by-state (admin-panel-v2).
 *
 * Primary contract: `{ success, data: StateRow[] }` — same envelope as other
 * financials KPI endpoints. Each row carries state identity plus withdraw,
 * revenue, and deposit amounts (field names may use withdrawal_amount /
 * deposit_amount aliases).
 *
 * Fallback: a keyed map `{ "Colorado": { revenue, ... } }` from legacy mocks;
 * entries without recognizable financial fields are omitted.
 */
export const parseFinancialsByStateResponse = (
  body: unknown,
): FinancialsStateRow[] => {
  const payload = (body as { data?: unknown })?.data ?? body;

  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload
      .map((entry, index) => {
        if (!isFinancialsStateEntry(entry)) return null;

        const abbr =
          typeof entry.state === "string"
            ? entry.state.trim().toUpperCase()
            : typeof entry.abbr === "string"
              ? entry.abbr.trim().toUpperCase()
              : `S${index + 1}`;
        return mapEntryToRow(entry.name ?? abbr, abbr, entry);
      })
      .filter((row): row is FinancialsStateRow => row !== null);
  }

  if (typeof payload !== "object") return [];

  return Object.entries(payload as Record<string, unknown>)
    .filter(([, value]) => isFinancialsStateEntry(value))
    .map(([key, value]) =>
      mapEntryToRow(key, key.slice(0, 2).toUpperCase(), value as RawFinancialsStateRow),
    );
};

export const fetchFinancialsByState = async (
  filters: FinancialsByStateFilters,
): Promise<FinancialsStateRow[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.FINANCIALS_BY_STATE, {
    params: filters,
  });
  return parseFinancialsByStateResponse(response?.data);
};

export const useGetFinancialsByState = (filters: FinancialsByStateFilters) => {
  const { start_date, end_date } = filters;

  return useQuery<FinancialsStateRow[], Error>({
    queryKey: ["FINANCIALS_BY_STATE", start_date, end_date],
    queryFn: () => fetchFinancialsByState(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
